"use client";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { resolvePlanIconComponent } from "@/lib/planIcons";
import { formatInvestLimitSummary, resolveInvestLimit } from "@/lib/planInvestLimit";

const DEFAULT_ACCENT = "#F59E0B";
const HEX_RE = /^#([0-9a-f]{3}){1,2}$/i;

function safeAccent(value) {
  return typeof value === "string" && HEX_RE.test(value) ? value : DEFAULT_ACCENT;
}

/** Convert `#RRGGBB` (or `#RGB`) into `r,g,b` for inline `rgba()` styling. */
function hexToRgbTuple(hex) {
  const v = safeAccent(hex).replace("#", "");
  const full = v.length === 3 ? v.split("").map((c) => c + c).join("") : v;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

export default function Plans() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await fetch("/api/plans");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || cancelled) return;
      setPlans(Array.isArray(data.plans) ? data.plans : []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = useMemo(
    () =>
      plans.map((p) => {
        const limit = resolveInvestLimit(p);
        return {
          icon: resolvePlanIconComponent(p.iconKey, p.name),
          title: p.name,
          tag: p.duration,
          duration: p.months,
          minPrincipal: p.minPrincipal,
          maxPrincipal: p.maxPrincipal,
          interestRate: p.interestRate,
          dailyProfit: p.dailyProfit,
          earningDays: p.earningDays,
          expectedProfit: p.expectedProfit,
          totalReturn: p.totalReturn,
          investLimit: limit.enabled ? formatInvestLimitSummary(limit) : "",
          accentColor: safeAccent(p.accentColor),
          description:
            (typeof p.description === "string" && p.description.trim()) ||
            "N/A",
        };
      }),
    [plans]
  );

  return (
    <section
      id="plans"
      className="bg-bg-primary 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.1)] px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            Investment Plans
          </span>
          <h2 className="font-display mb-4 text-[clamp(32px,5vw,56px)]">
            Choose Your <span className="text-ember">Growth Strategy</span>
          </h2>
          <p className="mx-auto max-w-[520px] text-base text-text-muted sm:text-lg">
            Select a plan that aligns with your financial goals and risk
            appetite.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((plan, i) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-base group relative h-full overflow-hidden"
                style={{
                  borderColor: plan.accentColor
                    ? `${plan.accentColor}33`
                    : undefined,
                }}
              >
                <div className="flex h-full flex-col p-4 2xl:p-8 xl:p-7 lg:p-6 md:p-5 sm:p-4">
                  <div
                    style={{
                      background: `rgba(${hexToRgbTuple(plan.accentColor)},0.12)`,
                      border: `1px solid ${plan.accentColor}33`,
                      color: plan.accentColor,
                    }}
                    className="absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-bold"
                  >
                    {plan.tag}
                  </div>

                  {/* Top Content: Grows to push remaining items to bottom */}
                  <div className="grow">
                    <div
                      style={{
                        background: `${plan.accentColor}18`,
                        border: `1px solid ${plan.accentColor}30`,
                      }}
                      className="mb-5 flex h-[52px] w-[52px] items-center justify-center rounded-xl shadow-[inset_0_0_12px_rgba(255,255,255,0.05)] transition-transform duration-300 group-hover:scale-110"
                    >
                      <Icon size={24} color={plan.accentColor} />
                    </div>

                    {/* Header content with min-height to align subsequent attribute grids */}
                    <div className="min-h-[140px] sm:min-h-[150px] lg:min-h-[145px] xl:min-h-[160px]">
                      <h3 className="font-display mb-2 w-full wrap-break-word text-[19px] leading-tight text-text-primary sm:text-[22px] xl:text-[18px] 2xl:text-[22px]">
                        {plan.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-text-muted">
                        {plan.description}
                      </p>
                    </div>

                    <div className="mb-7 grid grid-cols-2 gap-3">
                      {[
                        { label: "Duration", value: plan.duration },
                        { label: "Min. Principal", value: plan.minPrincipal },
                        { label: "Max. Principal", value: plan.maxPrincipal },
                        { label: "Interest Rate", value: plan.interestRate },
                        ...(plan.dailyProfit
                          ? [{ label: "Daily Profit", value: plan.dailyProfit }]
                          : []),
                        ...(Number(plan.earningDays) > 0
                          ? [{ label: "Earning Days", value: String(plan.earningDays) }]
                          : []),
                        ...(plan.expectedProfit
                          ? [{ label: "Expected Profit", value: plan.expectedProfit }]
                          : []),
                        ...(plan.investLimit
                          ? [{ label: "Invest limit", value: plan.investLimit }]
                          : []),
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-lg bg-[rgba(255,255,255,0.03)] p-3 border border-white/5"
                        >
                          <div className="mb-1 text-[11px] uppercase tracking-wider text-text-muted/70">
                            {item.label}
                          </div>
                          <div className="text-sm font-semibold text-text-primary">
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom Content: Aligned across all cards via parent grow */}
                  <div className="mt-auto pt-2">
                    <div
                      style={{
                        background: `linear-gradient(135deg, ${plan.accentColor}18, ${plan.accentColor}05)`,
                        border: `1px solid ${plan.accentColor}25`,
                      }}
                      className="mb-6 flex items-center justify-between rounded-xl px-4 py-3.5 shadow-lg shadow-black/20"
                    >
                      <div className="flex items-center gap-2">
                        <TrendingUp size={16} color={plan.accentColor} />
                        <span className="text-xs text-text-muted sm:text-sm font-medium">
                          Total Return
                        </span>
                      </div>
                      <span
                        className="font-display text-lg font-extrabold"
                        style={{ color: plan.accentColor }}
                      >
                        {plan.totalReturn}
                      </span>
                    </div>

                    <a
                      href="#calculator"
                      onClick={() => {
                        // Hand off the picked plan to <StakeCalculator />. The component
                        // listens for `stake-calc-select` AND reads sessionStorage on mount,
                        // so this works whether the calculator is already mounted or not.
                        try {
                          sessionStorage.setItem("stake-calc-plan", plan.title);
                        } catch {
                          /* sessionStorage may be blocked — fall back to the event below */
                        }
                        try {
                          window.dispatchEvent(
                            new CustomEvent("stake-calc-select", { detail: plan.title })
                          );
                        } catch {
                          /* CustomEvent not supported — sessionStorage path will handle it */
                        }
                      }}
                      style={{
                        background: `linear-gradient(135deg, ${plan.accentColor}, ${plan.accentColor}dd)`,
                        color: "#fff",
                      }}
                      className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl px-4 py-4 text-center font-display text-sm font-bold uppercase tracking-wider no-underline transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg shadow-black/20"
                    >
                      <>
                        <span>Calculate Returns</span>
                        <ArrowRight
                          size={18}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </>
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
